(() => {
  if (window.AlphaPCLeadAttribution) return;

  const FIRST_TOUCH_KEY = 'alpha_pc_first_touch_v1';
  const SESSION_TOUCH_KEY = 'alpha_pc_session_touch_v1';
  const FIRST_TOUCH_MAX_AGE = 90 * 24 * 60 * 60 * 1000;
  const ATTRIBUTION_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'gbraid',
    'wbraid',
    'msclkid',
    'fbclid',
    'ttclid',
    'li_fat_id',
    'gad_source'
  ];

  const safeStorage = {
    get(storage, key) {
      try {
        return storage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(storage, key, value) {
      try {
        storage.setItem(key, value);
      } catch (error) {
        return;
      }
    },
    remove(storage, key) {
      try {
        storage.removeItem(key);
      } catch (error) {
        return;
      }
    }
  };

  function cleanValue(value, maxLength = 180) {
    return String(value || '').replace(/[\r\n\t]+/g, ' ').trim().slice(0, maxLength);
  }

  function normalizedHost(hostname) {
    return cleanValue(hostname, 180).toLowerCase().replace(/^www\./, '');
  }

  function safeUrl(value, includeAttributionParams = false) {
    try {
      const parsed = new URL(value, window.location.origin);
      const output = new URL(`${parsed.origin}${parsed.pathname}`);
      if (includeAttributionParams) {
        ATTRIBUTION_PARAMS.forEach((key) => {
          const parameter = parsed.searchParams.get(key);
          if (parameter) output.searchParams.set(key, cleanValue(parameter, 256));
        });
      }
      return output.toString();
    } catch (error) {
      return '';
    }
  }

  function readTouch(storage, key, maxAge) {
    const raw = safeStorage.get(storage, key);
    if (!raw) return null;
    try {
      const touch = JSON.parse(raw);
      const capturedAt = Date.parse(touch.capturedAt || '');
      if (maxAge && (!capturedAt || Date.now() - capturedAt > maxAge)) {
        safeStorage.remove(storage, key);
        return null;
      }
      return touch;
    } catch (error) {
      safeStorage.remove(storage, key);
      return null;
    }
  }

  function socialSourceFromHost(host) {
    if (/^(facebook\.com|m\.facebook\.com|l\.facebook\.com)$/.test(host)) return 'facebook';
    if (/^(instagram\.com|l\.instagram\.com)$/.test(host)) return 'instagram';
    if (/(^|\.)linkedin\.com$/.test(host)) return 'linkedin';
    if (/(^|\.)(x\.com|twitter\.com|t\.co)$/.test(host)) return 'x';
    if (/(^|\.)tiktok\.com$/.test(host)) return 'tiktok';
    if (/(^|\.)(youtube\.com|youtu\.be)$/.test(host)) return 'youtube';
    if (/(^|\.)reddit\.com$/.test(host)) return 'reddit';
    if (/(^|\.)pinterest\.(com|ca)$/.test(host)) return 'pinterest';
    return '';
  }

  function searchSourceFromHost(host) {
    if (/(^|\.)google\./.test(host)) return 'google';
    if (/(^|\.)bing\.com$/.test(host)) return 'bing';
    if (/(^|\.)duckduckgo\.com$/.test(host)) return 'duckduckgo';
    if (/(^|\.)search\.yahoo\.com$/.test(host)) return 'yahoo';
    if (/(^|\.)ecosia\.org$/.test(host)) return 'ecosia';
    return '';
  }

  function channelLabel(source, medium, referrerHost, clickIds) {
    const normalizedSource = source.toLowerCase();
    const normalizedMedium = medium.toLowerCase();
    const paidMedium = /^(cpc|ppc|paid|paid_search|paid-social|paid_social|display|retargeting)$/.test(normalizedMedium);

    if (clickIds.gclid || clickIds.gbraid || clickIds.wbraid || clickIds.gad_source || (normalizedSource === 'google' && paidMedium)) return 'Google Ads';
    if (clickIds.msclkid || (normalizedSource === 'bing' && paidMedium)) return 'Microsoft Ads';
    if (clickIds.fbclid || (/^(facebook|instagram|meta)$/.test(normalizedSource) && paidMedium)) return 'Meta Ads';
    if (clickIds.ttclid || (normalizedSource === 'tiktok' && paidMedium)) return 'TikTok Ads';
    if (clickIds.li_fat_id || (normalizedSource === 'linkedin' && paidMedium)) return 'LinkedIn Ads';
    if (normalizedMedium === 'organic') return `${normalizedSource || 'Search'} organic search`;
    if (/^(email|newsletter)$/.test(normalizedMedium)) return 'Email';
    if (/^(social|organic_social)$/.test(normalizedMedium)) return `${normalizedSource || 'Social'} social`;
    if (normalizedSource && normalizedMedium) return `${source} / ${medium}`;
    if (normalizedSource) return source;
    if (referrerHost) return `Referral from ${referrerHost}`;
    return 'Direct';
  }

  function buildCurrentTouch() {
    const params = new URLSearchParams(window.location.search);
    const clickIds = {
      gclid: cleanValue(params.get('gclid'), 256),
      gbraid: cleanValue(params.get('gbraid'), 256),
      wbraid: cleanValue(params.get('wbraid'), 256),
      msclkid: cleanValue(params.get('msclkid'), 256),
      fbclid: cleanValue(params.get('fbclid'), 256),
      ttclid: cleanValue(params.get('ttclid'), 256),
      li_fat_id: cleanValue(params.get('li_fat_id'), 256),
      gad_source: cleanValue(params.get('gad_source'), 64)
    };
    const currentHost = normalizedHost(window.location.hostname);
    const referrerUrl = safeUrl(document.referrer);
    let referrerHost = '';
    try {
      referrerHost = normalizedHost(new URL(referrerUrl).hostname);
    } catch (error) {
      referrerHost = '';
    }
    if (referrerHost === currentHost) referrerHost = '';

    let source = cleanValue(params.get('utm_source'));
    let medium = cleanValue(params.get('utm_medium'));
    if (clickIds.gclid || clickIds.gbraid || clickIds.wbraid || clickIds.gad_source) {
      source = source || 'google';
      medium = medium || 'cpc';
    } else if (clickIds.msclkid) {
      source = source || 'bing';
      medium = medium || 'cpc';
    } else if (clickIds.fbclid) {
      source = source || 'meta';
      medium = medium || 'paid_social';
    } else if (clickIds.ttclid) {
      source = source || 'tiktok';
      medium = medium || 'paid_social';
    } else if (clickIds.li_fat_id) {
      source = source || 'linkedin';
      medium = medium || 'paid_social';
    } else if (!source && referrerHost) {
      const searchSource = searchSourceFromHost(referrerHost);
      const socialSource = socialSourceFromHost(referrerHost);
      if (searchSource) {
        source = searchSource;
        medium = 'organic';
      } else if (socialSource) {
        source = socialSource;
        medium = 'social';
      } else {
        source = referrerHost;
        medium = 'referral';
      }
    }

    const hasCampaignSignal = ATTRIBUTION_PARAMS.some((key) => params.has(key));
    const touch = {
      channel: channelLabel(source, medium, referrerHost, clickIds),
      source: source || '(direct)',
      medium: medium || '(none)',
      campaign: cleanValue(params.get('utm_campaign')),
      term: cleanValue(params.get('utm_term')),
      content: cleanValue(params.get('utm_content')),
      landingPage: safeUrl(window.location.href, true),
      referrer: referrerUrl,
      referrerHost,
      clickIds,
      capturedAt: new Date().toISOString()
    };

    return { touch, hasCampaignSignal, hasExternalReferrer: Boolean(referrerHost) };
  }

  const current = buildCurrentTouch();
  let sessionTouch = readTouch(window.sessionStorage, SESSION_TOUCH_KEY);
  if (!sessionTouch || current.hasCampaignSignal || current.hasExternalReferrer) {
    sessionTouch = current.touch;
    safeStorage.set(window.sessionStorage, SESSION_TOUCH_KEY, JSON.stringify(sessionTouch));
  }

  let firstTouch = readTouch(window.localStorage, FIRST_TOUCH_KEY, FIRST_TOUCH_MAX_AGE);
  if (!firstTouch) {
    firstTouch = sessionTouch;
    safeStorage.set(window.localStorage, FIRST_TOUCH_KEY, JSON.stringify(firstTouch));
  }

  function findNamedField(form, name) {
    return Array.from(form.elements).find((field) => field.name === name) || null;
  }

  function setHiddenField(form, label, value) {
    const name = `contact[${label}]`;
    let field = findNamedField(form, name);
    if (field && field.type !== 'hidden') return;
    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = name;
      field.dataset.alphaAttribution = 'true';
      form.appendChild(field);
    }
    field.value = cleanValue(value, 1000);
  }

  function clickIdSummary(clickIds) {
    return Object.entries(clickIds || {})
      .filter(([, value]) => value)
      .map(([key, value]) => `${key.toUpperCase()}: ${value}`)
      .join(' | ');
  }

  function attributionSummary(touch) {
    const pieces = [touch.channel, `${touch.source} / ${touch.medium}`];
    if (touch.campaign) pieces.push(`Campaign: ${touch.campaign}`);
    return pieces.join(' | ');
  }

  function isShopifyContactForm(form) {
    return Boolean(form.querySelector('input[name="form_type"][value="contact"]'));
  }

  function applyToForm(form) {
    if (!form || !isShopifyContactForm(form)) return;

    setHiddenField(form, 'Lead Source', sessionTouch.channel);
    setHiddenField(form, 'Source / Medium', `${sessionTouch.source} / ${sessionTouch.medium}`);
    setHiddenField(form, 'Attribution Summary', attributionSummary(sessionTouch));
    setHiddenField(form, 'First Touch Source', firstTouch.channel);
    setHiddenField(form, 'First Touch Source / Medium', `${firstTouch.source} / ${firstTouch.medium}`);
    setHiddenField(form, 'First Touch Landing Page', firstTouch.landingPage);
    setHiddenField(form, 'First Touch Referrer', firstTouch.referrer || 'Direct');
    setHiddenField(form, 'Landing Page', sessionTouch.landingPage);
    setHiddenField(form, 'Referring Page', sessionTouch.referrer || 'Direct');
    setHiddenField(form, 'Form Page', safeUrl(window.location.href, true));
    setHiddenField(form, 'UTM Source', sessionTouch.source === '(direct)' ? '' : sessionTouch.source);
    setHiddenField(form, 'UTM Medium', sessionTouch.medium === '(none)' ? '' : sessionTouch.medium);
    setHiddenField(form, 'UTM Campaign', sessionTouch.campaign);
    setHiddenField(form, 'UTM Term', sessionTouch.term);
    setHiddenField(form, 'UTM Content', sessionTouch.content);
    setHiddenField(form, 'GCLID', sessionTouch.clickIds?.gclid);
    setHiddenField(form, 'GBRAID', sessionTouch.clickIds?.gbraid);
    setHiddenField(form, 'WBRAID', sessionTouch.clickIds?.wbraid);
    setHiddenField(form, 'Microsoft Click ID', sessionTouch.clickIds?.msclkid);
    setHiddenField(form, 'Meta Click ID', sessionTouch.clickIds?.fbclid);
    setHiddenField(form, 'TikTok Click ID', sessionTouch.clickIds?.ttclid);
    setHiddenField(form, 'LinkedIn Click ID', sessionTouch.clickIds?.li_fat_id);
    setHiddenField(form, 'Google Ads Source', sessionTouch.clickIds?.gad_source);
    setHiddenField(form, 'Ad Click ID Summary', clickIdSummary(sessionTouch.clickIds));
    setHiddenField(form, 'Attribution Captured At', sessionTouch.capturedAt);

    if (form.dataset.alphaAttributionReady !== 'true') {
      form.dataset.alphaAttributionReady = 'true';
      form.addEventListener('submit', () => applyToForm(form), { capture: true });
    }
  }

  function initializeForms(root = document) {
    const forms = root.matches?.('form') ? [root] : Array.from(root.querySelectorAll?.('form') || []);
    forms.forEach(applyToForm);
  }

  window.AlphaPCLeadAttribution = {
    applyToForm,
    initializeForms,
    firstTouch,
    sessionTouch
  };

  const initialize = () => initializeForms(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initializeForms(event.target));

  if ('MutationObserver' in window) {
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        initialize();
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
